<template>
  <div>

    <!-- ── Mobile Top Bar ── -->
    <div class="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40 shadow-sm">
      <div class="flex items-center gap-2">
        <img src="../assets/abc-logo.png" alt="ABC Tax Advisory" class="h-7 w-auto object-contain" />
        <div class="flex flex-col leading-tight">
          <span class="text-secondary text-sm font-bold">ABC Tax Advisory</span>
          <span class="text-slate-400 text-xs font-medium">Client Portal</span>
        </div>
      </div>
      <button @click="mobileOpen = true" class="text-slate-500 hover:text-secondary p-1">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      </button>
    </div>

    <!-- ── Mobile overlay backdrop ── -->
    <transition name="fade">
      <div
        v-if="mobileOpen"
        class="md:hidden fixed inset-0 bg-black/50 z-40"
        @click="mobileOpen = false"/>
    </transition>

    <!-- ── Sidebar (desktop: always visible | mobile: slide-in drawer) ── -->
    <transition name="slide">
      <aside
        v-show="mobileOpen || isDesktop"
        class="fixed md:static top-0 left-0 h-full md:h-screen w-64 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-hidden z-50 shadow-xl md:shadow-none">

        <!-- Logo -->
        <div class="h-16 flex items-center px-6 border-b border-slate-100">
          <div class="flex items-center gap-3">
            <img src="../assets/abc-logo.png" alt="ABC Tax Advisory" class="h-8 w-auto object-contain" />
            <div class="flex flex-col">
              <h1 class="text-secondary text-sm font-bold leading-tight tracking-tight">ABC Tax Advisory</h1>
              <span class="text-slate-400 text-xs font-medium">Client Portal</span>
            </div>
          </div>
        </div>

        <!-- Client Profile -->
        <div class="px-6 pt-6 pb-6 border-b border-slate-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3 min-w-0">
              <div class="h-10 w-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 ring-2 ring-slate-100">
                <span class="text-sm font-bold text-primary">{{ initials }}</span>
              </div>
              <div class="min-w-0">
                <p class="text-sm font-bold text-secondary truncate">{{ clientName }}</p>
                <p class="text-xs text-slate-500 truncate">Individual Client</p>
              </div>
            </div>
            <!-- Close button — mobile only -->
            <button @click="mobileOpen = false" class="md:hidden text-slate-400 hover:text-slate-600 ml-2 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-3 py-4 space-y-1 overflow-y-auto">

          <router-link
            to="/client/dashboard"
            @click="mobileOpen = false"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group"
            :class="isActive('dashboard') ? 'bg-secondary text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-secondary'">
            <span class="material-symbols-outlined" style="font-size: 20px;" :class="isActive('dashboard') ? '' : 'group-hover:text-primary'">home</span>
            Home
          </router-link>

          <router-link
            to="/client/documents"
            @click="mobileOpen = false"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group"
            :class="isActive('documents') ? 'bg-secondary text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-secondary'">
            <span class="material-symbols-outlined" style="font-size: 20px;" :class="isActive('documents') ? '' : 'group-hover:text-primary'">folder_copy</span>
            Documents
          </router-link>

          <router-link
            to="/client/messages"
            @click="mobileOpen = false"
            class="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group"
            :class="isActive('messages') ? 'bg-secondary text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-secondary'">
            <div class="flex items-center gap-3">
              <span class="material-symbols-outlined" style="font-size: 20px;" :class="isActive('messages') ? '' : 'group-hover:text-primary'">chat</span>
              Messages
            </div>
            <span v-if="unreadCount > 0" class="ml-auto bg-primary text-secondary text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {{ unreadCount }}
            </span>
          </router-link>

          <router-link
            to="/client/announcements"
            @click="mobileOpen = false"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group"
            :class="isActive('announcements') ? 'bg-secondary text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-secondary'">
            <span class="material-symbols-outlined" style="font-size: 20px;" :class="isActive('announcements') ? '' : 'group-hover:text-primary'">campaign</span>
            Announcements
          </router-link>

        </nav>

        <!-- Footer links -->
        <div class="px-3 pb-2">
          <div class="border-t border-slate-100 pt-3 space-y-1">
            <a href="https://abctaxadvisory.com.au" target="_blank"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-primary hover:bg-slate-50 transition-all">
              <span class="material-symbols-outlined" style="font-size:14px;">language</span>
              abctaxadvisory.com.au
            </a>
            <a href="tel:0286063665"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-primary hover:bg-slate-50 transition-all">
              <span class="material-symbols-outlined" style="font-size:14px;">call</span>
              02 8606 3665
            </a>
            <a href="https://abctaxadvisory.com.au/contact" target="_blank"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-primary hover:bg-slate-50 transition-all">
              <span class="material-symbols-outlined" style="font-size:14px;">support_agent</span>
              Contact Us
            </a>
          </div>
          <p class="text-[10px] text-slate-300 px-3 pt-2 pb-1">© 2025 ABC Tax Advisory Pty Ltd</p>
        </div>

        <!-- Bottom: Logout -->
        <div class="px-3 pb-5 space-y-1 border-t border-slate-100 pt-3">
          <button
            @click="handleLogout"
            class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:text-red-500 hover:bg-red-50 transition-all">
            <span class="material-symbols-outlined" style="font-size: 20px;">logout</span>
            Sign Out
          </button>
        </div>

      </aside>
    </transition>

    <!-- ── Mobile spacer so page content doesn't go under top bar ── -->
    <div class="md:hidden h-14 flex-shrink-0"></div>

  </div>
</template>

<script>
export default {
  name: 'ClientSidebar',

  data() {
    return {
      unreadCount: 0,
      unreadInterval: null,
      mobileOpen: false,
      isDesktop: window.innerWidth >= 768
    };
  },

  computed: {
    clientRecord() {
      try {
        const token = sessionStorage.getItem('clientSession');
        if (!token) return {};
        const [data] = token.split('.');
        if (!data) return {};
        const payload = JSON.parse(atob(data.replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp || Date.now() > payload.exp) return {};
        return payload;
      } catch { return {}; }
    },
    clientName() { return this.clientRecord?.name || 'Client'; },
    initials() {
      const parts = this.clientName.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return this.clientName.substring(0, 2).toUpperCase();
    }
  },

  async mounted() {
    await this.fetchUnreadCount();
    this.unreadInterval = setInterval(() => this.fetchUnreadCount(), 30000);
    this._onResize = () => { this.isDesktop = window.innerWidth >= 768; };
    window.addEventListener('resize', this._onResize);
  },

  beforeUnmount() {
    if (this.unreadInterval) clearInterval(this.unreadInterval);
    window.removeEventListener('resize', this._onResize);
  },

  methods: {
    isActive(page) { return this.$route.path.includes(page); },

    async fetchUnreadCount() {
      try {
        if (!this.clientName || this.clientName === 'Client') return;
        this.unreadCount = await this.$sharepoint.getClientUnreadCount(this.clientName);
      } catch { /* silent */ }
    },

    async handleLogout() {
      try {
        sessionStorage.removeItem('clientSession');
        await this.$sharepoint.logout();
      } catch {
        sessionStorage.removeItem('clientSession');
        this.$router.push('/login');
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.slide-enter-active, .slide-leave-active { transition: transform 0.25s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(-100%); }
</style>